import { useRef, useState } from 'react'
import {
  useApplyPriceImport,
  useDeletePriceProfile,
  usePreviewPriceFile,
  usePriceImports,
  usePriceProfiles,
  useSavePriceProfile,
  useStockSuggestions,
  useUndoPriceImport
} from '../../api/queries'
import type {
  PriceImport,
  PriceImportDetail,
  PriceImportLine,
  PriceImportProfile,
  PriceImportProfileInput
} from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'
import { formatDateTime } from '../../lib/format'

/**
 * Prisfiler fra grossisten (docs/DECISIONS.md beslutning 072).
 *
 * <b>Forhåndsvisningen er ikke en bekvemmelighed.</b> Én prisfil rører tusind
 * varer på én gang, og en forkert kolonne gør hver eneste pris i huset forkert
 * uden at fejle. Derfor viser siden altid først, hvad filen VILLE gøre - og de
 * store prisspring står øverst, for det er dér, en glemt rabat viser sig.
 */
export function PriceImportPage() {
  const { data: profiles, isLoading, error } = usePriceProfiles()
  const { data: imports } = usePriceImports()

  const [editing, setEditing] = useState<PriceImportProfile | 'new' | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  if (editing) {
    return (
      <ProfileForm
        profile={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    )
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Prisfiler
        </h1>
        <button className="btn btn--primary" onClick={() => setEditing('new')}>
          Ny opsætning
        </button>
      </div>

      <ImportCard profiles={profiles ?? []} />

      <Card title={`Opsætninger (${profiles?.length ?? 0})`}>
        <p className="small muted" style={{ marginTop: 0 }}>
          Én opsætning pr. leverandørs filformat. <strong>Kolonnerne peges ud én gang</strong> - så
          er en ny grossist en ny opsætning, ikke en ny udgave af systemet.
        </p>

        {(profiles?.length ?? 0) === 0 ? (
          <EmptyState>
            Ingen opsætninger endnu. Lav en, der passer til den fil, din grossist sender - så kan
            prisfilen læses ind bagefter.
          </EmptyState>
        ) : (
          <div className="stack" style={{ gap: 2 }}>
            {profiles!.map((profile) => (
              <ProfileRow key={profile.id} profile={profile} onOpen={() => setEditing(profile)} />
            ))}
          </div>
        )}
      </Card>

      <HistoryCard imports={imports ?? []} />
    </div>
  )
}

/** Selve indlæsningen: vælg fil og opsætning, se hvad den ville gøre, tryk. */
function ImportCard({ profiles }: { profiles: PriceImportProfile[] }) {
  const preview = usePreviewPriceFile()
  const apply = useApplyPriceImport()

  const fileRef = useRef<HTMLInputElement>(null)

  const [profileId, setProfileId] = useState<number | null>(null)
  const [detail, setDetail] = useState<PriceImportDetail | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<string | null>(null)

  const chosen = profiles.find((p) => p.id === profileId) ?? null

  async function readFile() {
    const file = fileRef.current?.files?.[0]

    if (!file) {
      setMessage('Vælg en fil.')
      return
    }

    if (!profileId) {
      setMessage('Vælg den opsætning, der passer til filen.')
      return
    }

    setMessage(null)
    setReceipt(null)
    setDetail(null)

    try {
      setDetail(await preview.mutateAsync({ file, profileId }))
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Filen kunne ikke læses.')
    }
  }

  async function run() {
    if (!detail) return

    setMessage(null)

    try {
      const result = await apply.mutateAsync(detail.import.id)
      setDetail(result)
      setReceipt(`${priser(result.import.changedCount)} er sat.`)

      if (fileRef.current) fileRef.current.value = ''
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Priserne kunne ikke sættes.')
    }
  }

  if (profiles.length === 0) {
    return null
  }

  const run_ = detail?.import
  const isPreview = run_?.state === 'preview'

  return (
    <Card title="Læs en prisfil ind">
      <div className="form-grid">
        <div>
          <div className="tiny muted">Opsætning</div>
          <select
            value={profileId ?? ''}
            onChange={(event) => setProfileId(event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">Vælg opsætning ...</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.discountPercent ? ` - ${p.discountPercent} % rabat` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="tiny muted">Fil</div>
          <input ref={fileRef} type="file" accept=".csv,.txt,.tsv,text/csv,text/plain" />
        </div>
      </div>

      {chosen && !chosen.discountPercent && (
        <div className="map-notice" style={{ marginTop: 12 }}>
          Opsætningen regner <strong>ingen rabat</strong>. Er kolonnen en bruttopris, bliver alle
          priser for høje - sæt rabatprocenten på opsætningen først.
        </div>
      )}

      <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <button className="btn btn--primary" disabled={preview.isPending} onClick={() => void readFile()}>
          {preview.isPending ? 'Læser ...' : 'Vis hvad filen gør'}
        </button>
        <span className="tiny muted" style={{ alignSelf: 'center' }}>
          Ingen priser ændres, før du har set listen.
        </span>
      </div>

      {message && (
        <div className="map-notice" style={{ marginTop: 12 }}>
          {message}
        </div>
      )}

      {receipt && (
        <div className="map-notice" style={{ marginTop: 12 }}>
          {receipt}
        </div>
      )}

      {detail && run_ && (
        <div style={{ marginTop: 16 }}>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
            <Stat label="Linjer i filen" value={run_.rowCount} />
            <Stat label="Fundet i kartoteket" value={run_.matchedCount} />
            <Stat label={isPreview ? 'Ville ændres' : 'Ændret'} value={run_.changedCount} />
            <Stat label="Store spring" value={run_.largeChangeCount} warn={run_.largeChangeCount > 0} />
          </div>

          {run_.largeChangeCount > 0 && (
            <div className="map-notice" style={{ marginTop: 12 }}>
              <strong>
                {priser(run_.largeChangeCount)} springer mere end 50 %.
              </strong>{' '}
              Springer mange på én gang, er filens pris formentlig en bruttopris, og rabatten
              mangler i opsætningen. De står øverst i listen.
            </div>
          )}

          <LineTable lines={detail.changes} title={isPreview ? 'Det her sker' : 'Det her skete'} />

          {detail.problems.length > 0 && (
            <LineTable
              lines={detail.problems}
              title={`Linjer der ikke gav en ændring (${detail.problemCount})`}
              problems
            />
          )}

          {isPreview && (
            <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button
                className="btn btn--primary"
                disabled={apply.isPending || run_.changedCount === 0}
                onClick={() => void run()}
              >
                {apply.isPending
                  ? 'Sætter priser ...'
                  : `Sæt ${priser(run_.changedCount)}`}
              </button>
              <button className="btn" disabled={apply.isPending} onClick={() => setDetail(null)}>
                Fortryd
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      <div
        className="small"
        style={{ fontWeight: 700, fontSize: 18, color: warn ? 'var(--danger, #b91c1c)' : undefined }}
      >
        {value}
      </div>
    </div>
  )
}

function LineTable({
  lines,
  title,
  problems
}: {
  lines: PriceImportLine[]
  title: string
  problems?: boolean
}) {
  if (lines.length === 0) return null

  return (
    <div style={{ marginTop: 16 }}>
      <div className="tiny muted" style={{ marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Linje</th>
              <th>Vare</th>
              <th style={{ textAlign: 'right' }}>Før</th>
              <th style={{ textAlign: 'right' }}>Efter</th>
              {problems && <th>Hvorfor ikke</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <td className="tiny muted">{line.rowNumber}</td>
                <td>
                  <div className="small">{line.itemName ?? line.fileName ?? '-'}</div>
                  <div className="tiny muted">{line.key}</div>
                </td>
                <td className="small" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {money(line.oldPrice)}
                </td>
                <td
                  className="small"
                  style={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    fontWeight: line.isLargeChange ? 700 : undefined,
                    color: line.isLargeChange ? 'var(--danger, #b91c1c)' : undefined
                  }}
                >
                  {money(line.newPrice)}
                  {line.isLargeChange && ' ⚠'}
                </td>
                {problems && <td className="tiny muted">{line.problem}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** "1 pris" eller "12 priser" - teksten skal kunne læses som en sætning. */
function priser(count: number) {
  return count === 1 ? '1 pris' : `${count} priser`
}

function money(value?: number | null) {
  return value === null || value === undefined
    ? '-'
    : `${value.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.`
}

/** Kørslerne, så en fejl kan findes og rulles tilbage. */
function HistoryCard({ imports }: { imports: PriceImport[] }) {
  const undo = useUndoPriceImport()
  const [message, setMessage] = useState<string | null>(null)

  const done = imports.filter((i) => i.state !== 'preview')

  if (done.length === 0) return null

  async function rollBack(run: PriceImport) {
    setMessage(null)

    try {
      const result = await undo.mutateAsync(run.id)
      setMessage(
        `${priser(result.restored)} er sat tilbage` +
          (result.skipped > 0
            ? `. ${result.skipped} blev sprunget over, fordi de er rettet i hånden siden - dem rører vi ikke.`
            : '.')
      )
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Kørslen kunne ikke rulles tilbage.')
    }
  }

  return (
    <Card title="Tidligere kørsler">
      {message && <div className="map-notice" style={{ marginBottom: 12 }}>{message}</div>}

      <div className="stack" style={{ gap: 2 }}>
        {done.map((run) => (
          <div key={run.id} className="row row--between device-row" style={{ gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div className="small">
                {run.fileName}
                {run.state === 'undone' && <span className="tiny muted"> · rullet tilbage</span>}
              </div>
              <div className="tiny muted">
                {run.profileName ?? 'Ukendt opsætning'}
                {run.discountPercent ? ` · ${run.discountPercent} % rabat` : ''} ·{' '}
                {priser(run.changedCount)} · {formatDateTime(run.appliedAt ?? run.createdAt)} ·{' '}
                {run.createdBy?.displayName ?? 'Ukendt'}
                {run.largeChangeCount > 0 &&
                  ` · ${run.largeChangeCount === 1 ? '1 stort spring' : `${run.largeChangeCount} store spring`}`}
              </div>
            </div>

            {run.state === 'applied' && (
              <button
                className="btn btn--sm"
                disabled={undo.isPending}
                onClick={() => void rollBack(run)}
              >
                Rul tilbage
              </button>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

function ProfileRow({ profile, onOpen }: { profile: PriceImportProfile; onOpen: () => void }) {
  return (
    <button className="row row--between device-row" onClick={onOpen} style={{ textAlign: 'left' }}>
      <div style={{ minWidth: 0 }}>
        <div className="small">{profile.name}</div>
        <div className="tiny muted">
          {profile.supplier ?? 'Alle leverandører'} · nøgle: {profile.keyColumn} · pris:{' '}
          {profile.priceColumn}
          {profile.discountPercent ? ` · ${profile.discountPercent} % rabat` : ' · ingen rabat'}
        </div>
      </div>
      <span className="tiny muted">Ret</span>
    </button>
  )
}

/** Opsætningen af én leverandørs filformat. */
function ProfileForm({ profile, onClose }: { profile: PriceImportProfile | null; onClose: () => void }) {
  const save = useSavePriceProfile()
  const remove = useDeletePriceProfile()
  const { data: suggestions } = useStockSuggestions()

  const [form, setForm] = useState<PriceImportProfileInput>({
    name: profile?.name ?? '',
    supplier: profile?.supplier ?? '',
    delimiter: profile?.delimiter ?? '',
    hasHeader: profile?.hasHeader ?? true,
    matchKey: profile?.matchKey ?? 'supplier_number',
    keyColumn: profile?.keyColumn ?? '',
    priceColumn: profile?.priceColumn ?? '',
    nameColumn: profile?.nameColumn ?? '',
    discountPercent: profile?.discountPercent ?? null,
    decimalComma: profile?.decimalComma ?? true
  })

  const [problem, setProblem] = useState<string | null>(null)

  function set<K extends keyof PriceImportProfileInput>(key: K, value: PriceImportProfileInput[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function submit() {
    setProblem(null)

    try {
      await save.mutateAsync({ id: profile?.id, input: form })
      onClose()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Opsætningen kunne ikke gemmes.')
    }
  }

  async function drop() {
    if (!profile) return

    setProblem(null)

    try {
      await remove.mutateAsync(profile.id)
      onClose()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Opsætningen kunne ikke fjernes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <h1 className="page-title" style={{ margin: 0 }}>
        {profile ? profile.name : 'Ny prisopsætning'}
      </h1>

      <Card title="Hvad hedder den, og hvem er den til">
        <div className="form-grid">
          <div>
            <div className="tiny muted">Navn</div>
            <input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              placeholder="Solar, månedsfil"
              maxLength={150}
            />
          </div>

          <div>
            <div className="tiny muted">Leverandør</div>
            <input
              list="price-suppliers"
              value={form.supplier ?? ''}
              onChange={(event) => set('supplier', event.target.value)}
              placeholder="Alle leverandører"
              maxLength={120}
            />
            <datalist id="price-suppliers">
              {(suggestions?.suppliers ?? []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Er den sat, kan filen kun sætte priser på den leverandørs varer. To grossister kan
              have det samme varenummer på hver sin vare.
            </div>
          </div>
        </div>
      </Card>

      <Card title="Hvilke kolonner er hvad">
        <div className="form-grid">
          <div>
            <div className="tiny muted">Varen kendes på</div>
            <select
              value={form.matchKey}
              onChange={(event) => set('matchKey', event.target.value as PriceImportProfileInput['matchKey'])}
            >
              <option value="supplier_number">Varenummer hos leverandøren</option>
              <option value="barcode">Stregkode (EAN)</option>
            </select>
          </div>

          <div>
            <div className="tiny muted">Kolonne med nøglen</div>
            <input
              value={form.keyColumn}
              onChange={(event) => set('keyColumn', event.target.value)}
              placeholder="Varenr."
              maxLength={100}
            />
          </div>

          <div>
            <div className="tiny muted">Kolonne med prisen</div>
            <input
              value={form.priceColumn}
              onChange={(event) => set('priceColumn', event.target.value)}
              placeholder="Pris"
              maxLength={100}
            />
          </div>

          <div>
            <div className="tiny muted">Kolonne med varens navn (valgfri)</div>
            <input
              value={form.nameColumn ?? ''}
              onChange={(event) => set('nameColumn', event.target.value)}
              placeholder="Beskrivelse"
              maxLength={100}
            />
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Bruges kun til at vise, hvad en linje handler om. <strong>Navnet i kartoteket
              røres ikke.</strong>
            </div>
          </div>
        </div>

        <div className="tiny muted" style={{ marginTop: 10 }}>
          Skriv kolonnens <strong>navn</strong> fra filens overskriftsrække. Grossister bytter om på
          rækkefølgen uden at sige det, men overskriften plejer at stå fast. Har filen ingen
          overskrift, skriv et nummer fra 1.
        </div>
      </Card>

      <Card title="Hvordan er filen skrevet">
        <div className="form-grid">
          <div>
            <div className="tiny muted">Skilletegn</div>
            <select
              value={form.delimiter ?? ''}
              onChange={(event) => set('delimiter', event.target.value)}
            >
              <option value="">Find det selv</option>
              <option value=";">Semikolon ( ; )</option>
              <option value=",">Komma ( , )</option>
              <option value="\t">Tabulator</option>
            </select>
          </div>

          <div>
            <div className="tiny muted">Decimaltegn</div>
            <select
              value={form.decimalComma ? 'comma' : 'point'}
              onChange={(event) => set('decimalComma', event.target.value === 'comma')}
            >
              <option value="comma">Komma - dansk (1.234,56)</option>
              <option value="point">Punktum - engelsk (1,234.56)</option>
            </select>
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Et forkert valg flytter kommaet tre pladser. Der gættes ikke.
            </div>
          </div>

          <div>
            <label className="row" style={{ gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.hasHeader}
                onChange={(event) => set('hasHeader', event.target.checked)}
              />
              <span className="small">Filen har en overskriftsrække</span>
            </label>
          </div>
        </div>
      </Card>

      <Card title="Er filens pris en bruttopris?">
        <p className="small" style={{ marginTop: 0 }}>
          Danske el-grossister sender som regel <strong>bruttopriser</strong>, ikke det, I faktisk
          betaler. Er kolonnen en bruttopris, og aftalen 45 %, skriv 45 her - så bliver kostprisen
          bruttoprisen gange 0,55. Er filens pris allerede nettoprisen, lad feltet stå tomt.
        </p>
        <p className="small muted">
          <strong>Systemet gætter aldrig, hvilken slags pris det er.</strong> Gjorde det det, kunne
          hver eneste pris i huset blive dobbelt så høj, uden at noget fejlede.
        </p>

        <div style={{ maxWidth: 220 }}>
          <div className="tiny muted">Rabat i procent</div>
          <input
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            max="99.99"
            value={form.discountPercent ?? ''}
            onChange={(event) =>
              set('discountPercent', event.target.value === '' ? null : Number(event.target.value))
            }
            placeholder="Ingen rabat"
          />
        </div>
      </Card>

      {problem && <div className="error-box">{problem}</div>}

      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn--primary" disabled={save.isPending} onClick={() => void submit()}>
          {save.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
        <button className="btn" onClick={onClose}>
          Fortryd
        </button>
        {profile && (
          <button
            className="btn btn--danger"
            disabled={remove.isPending}
            onClick={() => void drop()}
            style={{ marginLeft: 'auto' }}
          >
            Fjern opsætningen
          </button>
        )}
      </div>

      {profile && (
        <div className="tiny muted">
          Fjernes opsætningen, bliver de tidligere kørsler stående - de har navnet gemt og kan
          stadig læses.
        </div>
      )}
    </div>
  )
}
